// PRD.318 — Dental Treatment: Treatment Plan + Treatment Done.
//
// ONE section, two views:
//
//   Treatment Plan  → dental_treatments (patient-longitudinal, planned/in-progress)
//   Treatment Done  → dental_treatment_records (encounter-scoped, what happened)
//
// Marking a plan done does NOT move or copy its row: it creates a performed
// record for this encounter and then updates the plan's lifecycle status
// (PRD §10). A plan may accumulate several performed records across visits
// while staying in_progress.
//
// Suggestions carry TOOTH CONTEXT and a provenance label only. Nothing maps a
// chart condition or diagnosis to a treatment (PRD §16A.3) — the clinician
// types it.
//
// Reuse: `HistoryEntryEditor` (PRD.314/316) for the autosaving, dictating notes
// field; `ToothPickerModal` (PRD.316) for tooth selection; `summarizeDiagnosisTeeth`
// (PRD.317) for the compact `16 · 17 · 18 +3` label.

import { useCallback, useMemo, useRef, useState } from 'react';
import {
  ACTIVE_PLAN_STATUSES,
  DENTAL_TREATMENT_STATUS_LABELS,
  buildChartSuggestions,
  dentalToothLinkId,
  diffTeeth,
  filterUsedTreatmentSuggestions,
  formatSurfaceInitials,
  getMedicalHistorySettings,
  groupToothLinks,
  groupTreatmentSuggestions,
  parseToothStatesMap,
  qAppConfig,
  qDentalTreatmentRecordTeethByPatient,
  qDentalTreatmentRecordsByPatient,
  qDentalTreatmentTeethByPatient,
  qDentalTreatmentsByPatient,
  qDiagnosesByEncounter,
  qDiagnosisTeethByEncounter,
  qOdontogramChartsByEncounter,
  groupTeethByDiagnosis,
  readTreatmentStatus,
  selectActivePlan,
  selectPerformedRecords,
  selectRecordsForTreatment,
  summarizeDiagnosisTeeth,
  type AppConfigRow,
  type DentalTreatmentRecordRow,
  type DentalTreatmentRow,
  type DentalTreatmentStatus,
  type DiagnosisToothRow,
  type OdontogramChartRow,
  type PatientDiagnosisEntryRow,
  type PatientEncounterRow,
  type TreatmentSuggestion,
} from '@helios/data-access';
import { useAuth } from '@helios/auth-firebase-web';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { NavIcon, useBrandTokens } from '../../../shell/shared';
import { HistoryEntryEditor } from './HistoryEntryEditor';
import { ToothPickerModal } from './ToothPickerModal';

/** Border + padding above a compact field's first line — see ClinicalFindingsSection. */
const FIRST_LINE_OFFSET = 7;

/**
 * Provenance captured when a plan draft is opened from a suggestion, written
 * with the row so the suggestion can later be suppressed by exact identity.
 */
interface DraftMeta {
  source: string;
  sourceRef: string | null;
  diagnosisId: string | null;
  sourceLabel: string | null;
}

type View = 'plan' | 'done';

/** Which record a tooth picker is currently editing. */
type PickerTarget = { kind: 'plan' | 'record'; id: string } | null;

export function DentalTreatmentSection({
  encounter,
  patientId,
}: {
  encounter: PatientEncounterRow;
  patientId: string;
}) {
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';

  const [view, setView] = useState<View>('plan');
  const [picker, setPicker] = useState<PickerTarget>(null);

  /**
   * Unpersisted rows opened by "+ Add Treatment" / "+ Add Treatment Done".
   * They become records on their first meaningful save, so an abandoned click
   * never leaves a blank treatment behind — the PRD.316 rule.
   */
  const [planDraft, setPlanDraft] = useState<{ id: string; teeth: string[] } | null>(null);
  const [doneDraft, setDoneDraft] = useState<{
    id: string;
    teeth: string[];
    treatmentId: string | null;
  } | null>(null);
  const pendingRef = useRef(
    new Map<string, { kind: 'plan' | 'record'; teeth: string[]; treatmentId: string | null }>(),
  );
  const draftMetaRef = useRef(new Map<string, DraftMeta>());

  // ── Reads ──

  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const voiceSettings = useMemo(
    () => getMedicalHistorySettings(appConfigRows[0] ?? null),
    [appConfigRows],
  );

  const { data: treatmentRows = [] } = usePowerSyncQuery<DentalTreatmentRow>(
    qDentalTreatmentsByPatient(patientId),
  );
  const { data: planToothRows = [] } = usePowerSyncQuery<{
    id: string;
    tooth: string | null;
    dental_treatment_id: string | null;
  }>(qDentalTreatmentTeethByPatient(patientId));
  const { data: recordRows = [] } = usePowerSyncQuery<DentalTreatmentRecordRow>(
    qDentalTreatmentRecordsByPatient(patientId),
  );
  const { data: recordToothRows = [] } = usePowerSyncQuery<{
    id: string;
    tooth: string | null;
    treatment_record_id: string | null;
  }>(qDentalTreatmentRecordTeethByPatient(patientId));

  const planTeeth = useMemo(
    () => groupToothLinks(planToothRows, (r) => r.dental_treatment_id),
    [planToothRows],
  );
  const recordTeeth = useMemo(
    () => groupToothLinks(recordToothRows, (r) => r.treatment_record_id),
    [recordToothRows],
  );

  const activePlan = useMemo(() => selectActivePlan(treatmentRows), [treatmentRows]);
  // Treatment Done shows THIS encounter's work; a plan row's own history is
  // rendered inline beneath it and spans encounters.
  const encounterRecords = useMemo(
    () => selectPerformedRecords(recordRows.filter((r) => r.encounter_id === encounter.id)),
    [recordRows, encounter.id],
  );

  // ── Suggestions (Treatment Plan only — PRD §16A.7) ──

  const { data: chartRows = [] } = usePowerSyncQuery<OdontogramChartRow>(
    qOdontogramChartsByEncounter(encounter.id),
  );
  const { data: diagnosisRows = [] } = usePowerSyncQuery<PatientDiagnosisEntryRow>(
    qDiagnosesByEncounter(encounter.id),
  );
  const { data: diagnosisToothRows = [] } = usePowerSyncQuery<DiagnosisToothRow>(
    qDiagnosisTeethByEncounter(encounter.id),
  );

  const suggestions = useMemo(() => {
    const all: TreatmentSuggestion[] = [];

    // FROM CHART — reuses PRD.316's chart-suggestion derivation, so there is no
    // second reading of `tooth_states`. The condition label rides along as
    // provenance only.
    for (const chart of chartRows) {
      for (const s of buildChartSuggestions(chart.id, parseToothStatesMap(chart.tooth_states))) {
        all.push({
          ref: s.ref,
          source: 'chart',
          teeth: [s.tooth],
          label: s.label,
          surfaces: formatSurfaceInitials(s.surfaces),
        });
      }
    }

    // FROM DIAGNOSIS — only diagnoses that actually carry tooth context are
    // useful here, since the whole point is preselecting teeth. Degrades to
    // nothing when PRD.317 has no associations (PRD §16A.6).
    const teethByDiagnosis = groupTeethByDiagnosis(diagnosisToothRows);
    for (const d of diagnosisRows) {
      const teeth = teethByDiagnosis.get(d.id) ?? [];
      if (teeth.length === 0) continue;
      all.push({
        ref: d.id,
        source: 'diagnosis',
        teeth,
        label: d.label,
        surfaces: '',
        diagnosisId: d.id,
      });
    }

    return groupTreatmentSuggestions(filterUsedTreatmentSuggestions(all, treatmentRows));
  }, [chartRows, diagnosisRows, diagnosisToothRows, treatmentRows]);

  // ── Writes ──

  const insertToothLinks = useCallback(
    async (table: 'plan' | 'record', parentId: string, teeth: readonly string[]) => {
      if (!db) return;
      const now = new Date().toISOString();
      const sql =
        table === 'plan'
          ? `INSERT INTO dental_treatment_teeth
               (id, org_id, dental_treatment_id, patient_id, tooth, created_at, updated_at)
             VALUES (?, ?, ?, ?, ?, ?, ?)`
          : `INSERT INTO dental_treatment_record_teeth
               (id, org_id, treatment_record_id, patient_id, tooth, created_at, updated_at)
             VALUES (?, ?, ?, ?, ?, ?, ?)`;
      for (const tooth of teeth) {
        await db.execute(sql, [
          dentalToothLinkId(parentId, tooth),
          orgId,
          parentId,
          patientId,
          tooth,
          now,
          now,
        ]);
      }
    },
    [db, orgId, patientId],
  );

  /** Persist a tooth selection as a minimal diff — unchanged teeth write nothing. */
  const saveTeeth = useCallback(
    async (kind: 'plan' | 'record', parentId: string, next: string[]) => {
      if (!db) return;
      const current = (kind === 'plan' ? planTeeth.get(parentId) : recordTeeth.get(parentId)) ?? [];
      const { added, removed } = diffTeeth(current, next);
      const table = kind === 'plan' ? 'dental_treatment_teeth' : 'dental_treatment_record_teeth';
      for (const tooth of removed) {
        await db.execute(`DELETE FROM ${table} WHERE id = ?`, [dentalToothLinkId(parentId, tooth)]);
      }
      await insertToothLinks(kind, parentId, added);
    },
    [db, planTeeth, recordTeeth, insertToothLinks],
  );

  /**
   * Autosave a plan item's treatment text. A draft is INSERTed on its first
   * meaningful text together with any preselected teeth and provenance.
   */
  const savePlanTreatment = useCallback(
    async (text: string, id: string) => {
      if (!db) return;
      const trimmed = text.trim();
      const now = new Date().toISOString();
      const pending = pendingRef.current.get(id);

      if (pending?.kind === 'plan') {
        if (!trimmed) return;
        const meta = draftMetaRef.current.get(id);
        await db.execute(
          `INSERT INTO dental_treatments
             (id, org_id, clinic_id, patient_id, created_encounter_id, treatment, status,
              source, source_ref, source_diagnosis_id, source_label,
              created_by_user_id, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            id,
            orgId,
            null,
            patientId,
            encounter.id,
            trimmed,
            'planned',
            meta?.source ?? 'manual',
            meta?.sourceRef ?? null,
            meta?.diagnosisId ?? null,
            meta?.sourceLabel ?? null,
            user?.uid ?? null,
            now,
            now,
          ],
        );
        if (pending.teeth.length > 0) await insertToothLinks('plan', id, pending.teeth);
        pendingRef.current.delete(id);
        draftMetaRef.current.delete(id);
        setPlanDraft((d) => (d?.id === id ? null : d));
        return;
      }

      if (!trimmed) return;
      await db.execute(`UPDATE dental_treatments SET treatment = ?, updated_at = ? WHERE id = ?`, [
        trimmed,
        now,
        id,
      ]);
    },
    [db, orgId, patientId, encounter.id, user?.uid, insertToothLinks],
  );

  /** Autosave a performed record's treatment text (INSERT on first content). */
  const saveRecordTreatment = useCallback(
    async (text: string, id: string) => {
      if (!db) return;
      const trimmed = text.trim();
      const now = new Date().toISOString();
      const pending = pendingRef.current.get(id);

      if (pending?.kind === 'record') {
        if (!trimmed) return;
        await db.execute(
          `INSERT INTO dental_treatment_records
             (id, org_id, clinic_id, patient_id, encounter_id, dental_treatment_id,
              treatment, notes, performed_at, performed_by, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            id,
            orgId,
            null,
            patientId,
            encounter.id,
            pending.treatmentId,
            trimmed,
            null,
            now,
            user?.uid ?? null,
            now,
            now,
          ],
        );
        if (pending.teeth.length > 0) await insertToothLinks('record', id, pending.teeth);
        pendingRef.current.delete(id);
        setDoneDraft((d) => (d?.id === id ? null : d));
        return;
      }

      if (!trimmed) return;
      await db.execute(
        `UPDATE dental_treatment_records SET treatment = ?, updated_at = ? WHERE id = ?`,
        [trimmed, now, id],
      );
    },
    [db, orgId, patientId, encounter.id, user?.uid, insertToothLinks],
  );

  /** Notes only ever belong to a persisted performed record. */
  const saveRecordNotes = useCallback(
    async (text: string, id: string) => {
      if (!db || pendingRef.current.has(id)) return;
      await db.execute(
        `UPDATE dental_treatment_records SET notes = ?, updated_at = ? WHERE id = ?`,
        [text.trim() ? text : null, new Date().toISOString(), id],
      );
    },
    [db],
  );

  const setStatus = useCallback(
    async (id: string, status: DentalTreatmentStatus) => {
      if (!db) return;
      // Lifecycle only — the row never moves or is copied (PRD §9.2).
      await db.execute(`UPDATE dental_treatments SET status = ?, updated_at = ? WHERE id = ?`, [
        status,
        new Date().toISOString(),
        id,
      ]);
    },
    [db],
  );

  /**
   * Record work performed against a plan (PRD §10).
   *
   * Opens a Treatment Done draft pre-filled with the plan's treatment text and
   * tooth scope, and switches to that view. The clinician confirms the actual
   * performed scope — the planned teeth are a starting point, never an
   * assumption that all of them were treated. Status is changed separately,
   * because a visit may be one step of a multi-visit treatment.
   */
  const recordPerformed = useCallback(
    (t: DentalTreatmentRow) => {
      const id = crypto.randomUUID();
      const teeth = planTeeth.get(t.id) ?? [];
      pendingRef.current.set(id, { kind: 'record', teeth: [...teeth], treatmentId: t.id });
      setDoneDraft({ id, teeth: [...teeth], treatmentId: t.id });
      setView('done');
    },
    [planTeeth],
  );

  const startPlanDraft = useCallback((teeth: string[], meta?: DraftMeta) => {
    const id = crypto.randomUUID();
    pendingRef.current.set(id, { kind: 'plan', teeth: [...teeth], treatmentId: null });
    if (meta) draftMetaRef.current.set(id, meta);
    setPlanDraft({ id, teeth: [...teeth] });
    setView('plan');
  }, []);

  const startDoneDraft = useCallback(() => {
    const id = crypto.randomUUID();
    pendingRef.current.set(id, { kind: 'record', teeth: [], treatmentId: null });
    setDoneDraft({ id, teeth: [], treatmentId: null });
    setView('done');
  }, []);

  const removePlan = useCallback(
    async (id: string) => {
      if (pendingRef.current.has(id)) {
        pendingRef.current.delete(id);
        draftMetaRef.current.delete(id);
        setPlanDraft((d) => (d?.id === id ? null : d));
        return;
      }
      if (!db) return;
      // Cancelled, not deleted: performed records already reference this plan
      // and must keep their provenance (PRD §20).
      await setStatus(id, 'cancelled');
    },
    [db, setStatus],
  );

  const removeRecord = useCallback(
    async (id: string) => {
      if (pendingRef.current.has(id)) {
        pendingRef.current.delete(id);
        setDoneDraft((d) => (d?.id === id ? null : d));
        return;
      }
      if (!db) return;
      try {
        await db.execute(
          `DELETE FROM dental_treatment_record_teeth WHERE treatment_record_id = ?`,
          [id],
        );
      } catch (err) {
        console.warn('[dental-treatment] could not clear performed tooth scope', err);
      }
      await db.execute(`DELETE FROM dental_treatment_records WHERE id = ?`, [id]);
    },
    [db],
  );

  // ── Render helpers ──

  const planRows = useMemo(() => {
    const list = activePlan.map((t) => ({ row: t, isDraft: false }));
    if (planDraft && !activePlan.some((t) => t.id === planDraft.id)) {
      list.push({ row: { id: planDraft.id } as DentalTreatmentRow, isDraft: true });
    }
    return list;
  }, [activePlan, planDraft]);

  const doneRows = useMemo(() => {
    const list = encounterRecords.map((r) => ({ row: r, isDraft: false }));
    if (doneDraft && !encounterRecords.some((r) => r.id === doneDraft.id)) {
      list.push({ row: { id: doneDraft.id } as DentalTreatmentRecordRow, isDraft: true });
    }
    return list;
  }, [encounterRecords, doneDraft]);

  const toothButton = (
    kind: 'plan' | 'record',
    id: string,
    teeth: readonly string[],
    isDraft: boolean,
  ) => {
    const summary = summarizeDiagnosisTeeth([...teeth]);
    return (
      <button
        className="pc-finding-context"
        onClick={() => setPicker({ kind, id })}
        title={
          summary.hiddenCount > 0
            ? `Teeth: ${summary.fullLabel}`
            : 'Choose teeth for this treatment'
        }
        aria-label={
          summary.all.length > 0
            ? `Teeth ${summary.fullLabel}. Choose teeth`
            : 'No tooth association. Choose teeth'
        }
        style={{
          padding: 0,
          paddingTop: FIRST_LINE_OFFSET,
          border: 'none',
          background: 'transparent',
          color: summary.label ? 'var(--text)' : 'var(--text-muted)',
          fontSize: 'var(--font-size-xs)',
          lineHeight: 'var(--line-height-body)',
          fontWeight: summary.label ? 600 : 400,
          cursor: 'pointer',
          fontFamily: 'inherit',
          textAlign: 'left',
          whiteSpace: 'nowrap',
          alignSelf: 'flex-start',
          borderBottom: isDraft ? '1px dashed var(--border)' : undefined,
        }}
      >
        {summary.label || 'General'}
      </button>
    );
  };

  const addButton = (label: string, onClick: () => void) => (
    <div>
      <button
        onClick={onClick}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px',
          borderRadius: 'var(--radius-sm)',
          border: `1px solid ${primary}`,
          background: 'transparent',
          color: primary,
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-medium)',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        <NavIcon name="plus" />
        {label}
      </button>
    </div>
  );

  const pickerTeeth = picker
    ? (pendingRef.current.get(picker.id)?.teeth ??
      (picker.kind === 'plan' ? planTeeth.get(picker.id) : recordTeeth.get(picker.id)) ??
      [])
    : [];

  return (
    <div
      style={{
        padding: 'var(--sp-4) var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      {/* ── View switch ── */}
      <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
        {(
          [
            ['plan', 'Treatment Plan'],
            ['done', 'Treatment Done'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setView(key)}
            style={{
              padding: '4px 12px',
              borderRadius: 'var(--radius-full)',
              border: `1px solid ${view === key ? primary : 'var(--border)'}`,
              background: view === key ? primary : 'transparent',
              color: view === key ? '#fff' : 'var(--text-muted)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {view === 'plan' ? (
        <>
          {/* ── Suggested ──
              Tooth context and a provenance label. NEVER a proposed treatment:
              caries does not imply composite, pulpitis does not imply RCT
              (PRD §16A.3). Not shown in Treatment Done (PRD §16A.7). */}
          {suggestions.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text-muted)',
                }}
              >
                Suggested
              </span>
              {suggestions.map((group) => (
                <div
                  key={group.source}
                  style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                >
                  <span
                    style={{
                      fontSize: 'calc(var(--font-size-xs) * 0.85)',
                      color: 'var(--text-placeholder, var(--text-muted))',
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                    }}
                  >
                    {group.title}
                  </span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
                    {group.items.map((s) => (
                      <button
                        key={s.ref}
                        onClick={() =>
                          startPlanDraft(s.teeth, {
                            source: s.source,
                            sourceRef: s.source === 'chart' ? s.ref : null,
                            diagnosisId: s.diagnosisId ?? null,
                            sourceLabel: s.label,
                          })
                        }
                        title="Start a treatment with this tooth context"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '4px 10px',
                          borderRadius: 'var(--radius-full)',
                          border: `1px dashed ${primary}`,
                          background: 'transparent',
                          color: primary,
                          fontSize: 'var(--font-size-xs)',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>+</span>
                        {[s.teeth.join(' · '), s.surfaces || null, s.label]
                          .filter(Boolean)
                          .join(' · ')}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {planRows.length === 0 && (
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              No planned treatment.
            </div>
          )}

          {planRows.map(({ row, isDraft }) => {
            const teeth = isDraft ? (planDraft?.teeth ?? []) : (planTeeth.get(row.id) ?? []);
            const status = readTreatmentStatus(row.status);
            const visits = isDraft ? [] : selectRecordsForTreatment(recordRows, row.id);
            return (
              <div
                key={row.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  paddingBottom: 'var(--sp-2)',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <div
                  className="pc-finding-row"
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-2)' }}
                >
                  {toothButton('plan', row.id, teeth, isDraft)}

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <HistoryEntryEditor
                      identity={row.id}
                      storedValue={row.treatment ?? ''}
                      onSave={savePlanTreatment}
                      label="Planned treatment"
                      placeholder="Add treatment…"
                      voiceEnabled={voiceSettings.voiceToTextEnabled}
                      autoRowsMax={1}
                    />
                  </div>

                  {!isDraft && (
                    <select
                      value={status}
                      onChange={(e) =>
                        void setStatus(row.id, e.target.value as DentalTreatmentStatus)
                      }
                      aria-label="Treatment status"
                      style={{
                        marginTop: FIRST_LINE_OFFSET,
                        padding: '2px 6px',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border)',
                        background: 'var(--bg-surface)',
                        color: 'var(--text-muted)',
                        fontSize: 'calc(var(--font-size-xs) * 0.9)',
                        fontFamily: 'inherit',
                        cursor: 'pointer',
                      }}
                    >
                      {ACTIVE_PLAN_STATUSES.concat(['done', 'cancelled']).map((s) => (
                        <option key={s} value={s}>
                          {DENTAL_TREATMENT_STATUS_LABELS[s]}
                        </option>
                      ))}
                    </select>
                  )}

                  {!isDraft && (
                    <button
                      onClick={() => recordPerformed(row)}
                      title="Record what was performed at this visit"
                      style={{
                        marginTop: FIRST_LINE_OFFSET,
                        padding: '2px 8px',
                        borderRadius: 'var(--radius-sm)',
                        border: `1px solid ${primary}`,
                        background: 'transparent',
                        color: primary,
                        fontSize: 'calc(var(--font-size-xs) * 0.9)',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Record done
                    </button>
                  )}

                  <button
                    onClick={() => void removePlan(row.id)}
                    aria-label={isDraft ? 'Discard treatment' : 'Cancel treatment'}
                    title={isDraft ? 'Discard' : 'Cancel treatment'}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      marginTop: FIRST_LINE_OFFSET + 4,
                      padding: 4,
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                    }}
                  >
                    <NavIcon name="x" />
                  </button>
                </div>

                {/* Multi-visit progress: each performed record stays attached to
                    the encounter where the work happened (PRD §11). */}
                {visits.length > 0 && (
                  <div
                    style={{
                      paddingLeft: 76,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      fontSize: 'calc(var(--font-size-xs) * 0.9)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {visits.map((v) => (
                      <span key={v.id}>
                        {formatVisitDay(v.performed_at ?? v.created_at)} · {v.treatment}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {addButton('Add Treatment', () => startPlanDraft([]))}
        </>
      ) : (
        <>
          {doneRows.length === 0 && (
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              No treatment recorded for this visit.
            </div>
          )}

          {doneRows.map(({ row, isDraft }) => {
            const teeth = isDraft ? (doneDraft?.teeth ?? []) : (recordTeeth.get(row.id) ?? []);
            const fromPlan = isDraft
              ? doneDraft?.treatmentId != null
              : row.dental_treatment_id != null;
            return (
              <div
                key={row.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  paddingBottom: 'var(--sp-2)',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <div
                  className="pc-finding-row"
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-2)' }}
                >
                  {toothButton('record', row.id, teeth, isDraft)}

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <HistoryEntryEditor
                      identity={row.id}
                      storedValue={row.treatment ?? ''}
                      onSave={saveRecordTreatment}
                      label="Treatment performed"
                      placeholder="Treatment performed…"
                      voiceEnabled={voiceSettings.voiceToTextEnabled}
                      autoRowsMax={1}
                    />
                  </div>

                  {fromPlan && (
                    <span
                      title="Recorded against a treatment plan item"
                      style={{
                        marginTop: FIRST_LINE_OFFSET,
                        padding: '1px 6px',
                        borderRadius: 'var(--radius-full)',
                        background: 'var(--bg-surface-2)',
                        color: 'var(--text-muted)',
                        fontSize: 'calc(var(--font-size-xs) * 0.85)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      From plan
                    </span>
                  )}

                  <button
                    onClick={() => void removeRecord(row.id)}
                    aria-label="Remove treatment record"
                    title="Remove"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      marginTop: FIRST_LINE_OFFSET + 4,
                      padding: 4,
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                    }}
                  >
                    <NavIcon name="x" />
                  </button>
                </div>

                {/* Notes attach to a saved record only — there is nothing to
                    write them to until the treatment itself is stored. */}
                {!isDraft && (
                  <div style={{ paddingLeft: 76, paddingRight: 32 }}>
                    <HistoryEntryEditor
                      identity={`${row.id}:notes`}
                      storedValue={row.notes ?? ''}
                      onSave={(text) => saveRecordNotes(text, row.id)}
                      label="Treatment notes"
                      placeholder="Notes…"
                      voiceEnabled={voiceSettings.voiceToTextEnabled}
                      autoRowsMax={4}
                    />
                  </div>
                )}
              </div>
            );
          })}

          {addButton('Add Treatment Done', startDoneDraft)}
        </>
      )}

      {picker && (
        <ToothPickerModal
          open
          initialTeeth={pickerTeeth.map((tooth) => ({ tooth, surfaces: [] }))}
          toothStates={chartRows[0] ? parseToothStatesMap(chartRows[0].tooth_states) : {}}
          primary={primary}
          // PRD §15 — teeth only; surface context stays on the chart.
          showSurfaces={false}
          onClose={() => setPicker(null)}
          onSave={(teeth) => {
            const picked = teeth.map((t) => t.tooth);
            const pending = pendingRef.current.get(picker.id);
            if (pending) {
              pending.teeth = picked;
              if (picker.kind === 'plan') {
                setPlanDraft((d) => (d?.id === picker.id ? { ...d, teeth: picked } : d));
              } else {
                setDoneDraft((d) => (d?.id === picker.id ? { ...d, teeth: picked } : d));
              }
            } else {
              void saveTeeth(picker.kind, picker.id, picked);
            }
            setPicker(null);
          }}
        />
      )}
    </div>
  );
}

/** Short day label for the per-visit progress list. */
function formatVisitDay(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
